/**
 * ExecutiveBriefing — Mayor's live situation panel for the EX lens.
 *
 * Shows: live situation alerts · city vitals snapshot · strategic initiatives.
 * All data is sourced from live feeds; nothing is manufactured. Clearly marks
 * reference/modelled values.
 */

import { useState } from "react";
import type {
  ExecutiveSnapshot,
  WeatherSnapshot,
  AirQualityPoint,
  FallbackTier,
} from "@sikhio/shared";
import type { DamReading } from "@sikhio/shared";
import type { ReservoirStatus } from "./WaterPanel";
import type { AdapterHealth } from "../hooks/useSystemHealth";
import { PanelHeader } from "./PanelHeader";
import { execAqiBand, execAqiColor, fmt1, fmtInt, avgUsablePct } from "../lib/executive";
import { renderBriefMarkdown, copyToClipboard } from "../lib/executiveBriefMarkdown";
import { Arrow } from "./Arrow";

interface Props {
  executive: ExecutiveSnapshot | null;
  weather: WeatherSnapshot | null;
  airQuality: AirQualityPoint | null;
  openIncidents: number;
  /** False when Traffy/iTIC are scenario-empty — don't imply "0 open" is calm. */
  reportsLive?: boolean;
  /** Live Lam Takhong telemetry — the town's actual water supply. */
  dam: DamReading | null;
  /** Annual provincial reservoirs — context behind the headline dam figure. */
  reservoirs: ReservoirStatus[];
  adapterHealth?: AdapterHealth[];
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
  /**
   * Water feeds that resolved without usable data. Empty alerts + gaps must
   * never read as "all clear" — the Mayor needs an honest FEED GAP line.
   */
  waterGaps?: string[];
}

const LEVEL_COLOR: Record<string, string> = {
  critical: "var(--bad)",
  warning:  "var(--bad)",
  watch:    "var(--warn)",
  info:     "var(--data)",
};

const LEVEL_ICON: Record<string, string> = {
  critical: "▲",
  warning:  "▲",
  watch:    "◆",
  info:     "ℹ",
};

const STATUS_COLOR: Record<string, string> = {
  "on-track":  "var(--good)",
  "at-risk":   "var(--warn)",
  "delayed":   "var(--bad)",
  "completed": "var(--data)",
};


export function ExecutiveBriefing({
  executive,
  weather,
  airQuality,
  openIncidents,
  reportsLive = true,
  dam,
  reservoirs,
  adapterHealth,
  ageMinutes,
  fallbackTier,
  waterGaps = [],
}: Props) {
  const alerts = executive?.alerts ?? [];
  const initiatives = executive?.initiatives ?? [];
  const aqi = airQuality?.aqi ?? null;
  // The mayor's water vital is Lam Takhong today, not a provincial average:
  // the annual data.go.th dataset publishes no capacity %, and a mean across
  // 29 reservoirs hides the one that supplies this town. Fall back to the
  // provincial usable share only when the live dam feed is down.
  const damPct = dam?.pctNormal ?? null;
  const avgReservoir = damPct ?? avgUsablePct(reservoirs);

  // "Today" + clock in the header — a Mayor skimming the brief should
  // never have to ask "what day is this for?". The brief is current, but
  // a quick Thai date is the cheapest way to make that obvious.
  const now = new Date();
  const todayStr = now.toLocaleDateString("th-TH", {
    day: "numeric", month: "long", year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("th-TH", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const weekday = now.toLocaleDateString("th-TH", { weekday: "long" });

  // Copy-brief-to-clipboard: render the brief as Markdown, push to the
  // clipboard, flash a confirmation so the Mayor knows it landed.
  // Without the toast the user often clicks twice thinking the first
  // press did nothing.
  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");
  const handleCopy = async () => {
    const md = renderBriefMarkdown(
      { executive, weather, airQuality, openIncidents, dam },
      now,
    );
    const ok = await copyToClipboard(md);
    setCopyState(ok ? "ok" : "err");
    window.setTimeout(() => setCopyState("idle"), 2200);
  };

  const damWatch =
    dam &&
    (dam.alertLevel === "critical-low" || dam.alertLevel === "low" || dam.alertLevel === "watch-low" ||
     dam.alertLevel === "critical-high" || dam.alertLevel === "high" || dam.alertLevel === "watch-high");

  return (
    <div className="executive-briefing">
      {/* Header */}
      <PanelHeader
        title="สรุปสถานการณ์ · EXECUTIVE BRIEF"
        source="live-feeds"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        actions={
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              className="mono caption"
              style={{ color: "var(--text-3)", textAlign: "right", lineHeight: 1.25 }}
              title={`Briefing generated ${todayStr} ${timeStr}`}
            >
              <span style={{ display: "block" }}>{weekday}</span>
              <span style={{ display: "block" }}>{todayStr} · {timeStr}</span>
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className={`exec-copy mono caption ${copyState !== "idle" ? `is-${copyState}` : ""}`}
              aria-label="คัดลอกสรุปไปยังคลิปบอร์ด (Copy brief to clipboard)"
              title="คัดลอกสรุปไปยังคลิปบอร์ด · Copy brief to clipboard (Markdown)"
            >
              {copyState === "ok"  ? "✓ คัดลอกแล้ว" : copyState === "err" ? "✗ ล้มเหลว" : "⎘ คัดลอก"}
            </button>
          </span>
        }
      />

      {/* Situation alerts — only claim calm when live feeds agree. */}
      <div className="exec-briefing-section">
        <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>สถานการณ์ · SITUATION</span>
        {alerts.length === 0 ? (
          <div
            className="exec-briefing-nominal mono caption"
            role="status"
            style={waterGaps.length > 0 ? { color: "var(--warn)" } : undefined}
          >
            {waterGaps.length > 0
              ? `◆ ฟีดน้ำไม่ครบ · WATER FEED GAP: ${waterGaps.join(" · ")} — ไม่ยืนยันว่าสงบ`
              : damWatch
              ? `◆ เฝ้าระวังน้ำ · ลำตะคอง ${damPct != null ? `${Math.round(damPct)}%` : ""} (${dam?.alertLevel})`
              : "✓ ไม่มีสัญญาณแจ้งเตือนจากฟีดสด · No active alerts from live feeds"}
          </div>
        ) : (
          <ul className="exec-briefing-alerts" role="list" aria-label="Active situation alerts">
            {alerts.map((a) => (
              <li key={a.id} className="exec-briefing-alert"
                  style={{ borderLeftColor: LEVEL_COLOR[a.level] ?? "var(--line)" }}>
                <div className="exec-briefing-alert-row">
                  <span className="mono" style={{ color: LEVEL_COLOR[a.level] ?? "var(--text-2)", fontSize: "var(--size-eyebrow)", letterSpacing: "0.08em", fontWeight: 600 }}>
                    {LEVEL_ICON[a.level] ?? "·"} {a.level.toUpperCase()} · {a.category.toUpperCase()}
                  </span>
                </div>
                <div className="exec-briefing-alert-title">{a.title}</div>
                <div className="exec-briefing-alert-msg">{a.message}</div>
                {a.actionRequired && (
                  <div className="exec-briefing-alert-action mono">
                    <Arrow direction="right" /> {a.actionRequired}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* City vitals */}
      <div className="exec-briefing-section">
        <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>ชีพจรเมือง · CITY VITALS</span>
        <div className="stat-detail-grid" role="group" aria-label="City vital statistics">
          <div>
            <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>ค่าฝุ่น</span>
            <span className="mono" style={{ color: aqi != null ? execAqiColor(aqi) : "var(--text-2)" }}>
              {fmtInt(aqi)}{aqi != null ? ` · ${execAqiBand(aqi)}` : ""}
            </span>
          </div>
          <div>
            <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>อุณหภูมิ</span>
            <span className="mono">{fmt1(weather?.tempC)}°C</span>
          </div>
          <div>
            <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>รายงาน</span>
            <span className="mono" style={{ color: !reportsLive ? "var(--text-3)" : openIncidents > 5 ? "var(--warn)" : "var(--text)" }}>
              {reportsLive ? `${openIncidents} OPEN` : "— · FEED GAP"}
            </span>
          </div>
          <div>
            {/* MSH: this cell was labelled "ลำตะคอง" and printed the same
                percentage the rail's hero already shows at the top — the same
                fact three times on one screen. When the dam feed is up the
                hero owns that number and this cell carries inflow instead, a
                measure of the same dam that nothing else on screen states.
                When the dam feed is DOWN the provincial average is a different
                number from a different source, so it stays. */}
            <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>
              {damPct != null ? "น้ำเข้า/วัน" : "RESERVOIRS"}
            </span>
            <span className="mono" style={{ color: avgReservoir != null && avgReservoir < 40 ? "var(--warn)" : "var(--text)" }}>
              {damPct != null
                ? dam?.inflowMcmDay != null ? `${dam.inflowMcmDay.toFixed(2)} ลบ.ม.` : "—"
                : avgReservoir != null ? `${Math.round(avgReservoir)}%` : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Strategic initiatives — only when a real tracker supplies them */}
      {initiatives.length > 0 && (
        <div className="exec-briefing-section">
          <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>แผนงาน</span>
          <ul className="exec-briefing-initiatives" role="list">
            {initiatives.map((init) => (
              <li key={init.id} className="exec-briefing-initiative">
                <div className="exec-briefing-init-row">
                  <span className="exec-briefing-init-name">{init.name}</span>
                  <span className="mono" style={{
                    fontSize: "var(--size-eyebrow)",
                    letterSpacing: "0.06em",
                    color: STATUS_COLOR[init.status] ?? "var(--text-3)",
                  }}>
                    {init.status.toUpperCase().replace("-", " ")}
                  </span>
                </div>
                <div className="exec-briefing-progress-track" role="progressbar"
                     aria-valuenow={init.progressPct} aria-valuemin={0} aria-valuemax={100}
                     aria-label={`${init.name}: ${init.progressPct}% complete`}>
                  <div className="exec-briefing-progress-fill"
                       style={{
                         width: `${init.progressPct}%`,
                         background: STATUS_COLOR[init.status] ?? "var(--data)",
                       }} />
                </div>
                <div className="mono" style={{ fontSize: "var(--size-eyebrow)", color: "var(--text-3)" }}>
                  {init.progressPct}% · {init.owner} · {init.deadline}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Data health — only shown when some adapters are not fully healthy */}
      {adapterHealth && adapterHealth.some(
        (a) => a.status === "degraded" || a.status === "down"
      ) && (
        <DataHealthSection adapters={adapterHealth} />
      )}
    </div>
  );
}

/** Compact adapter health list for the exec briefing. */
function DataHealthSection({ adapters }: { adapters: AdapterHealth[] }) {
  const unhealthy = adapters.filter((a) => a.status === "degraded" || a.status === "down");
  const healthy = adapters.filter((a) => a.status === "healthy").length;
  const total = adapters.length;

  return (
    <div className="exec-briefing-section" role="status" aria-label="Data feed health">
      <div className="spread" style={{ alignItems: "center", marginBottom: 4 }}>
        <span className="mono eyebrow" style={{ color: "var(--text-3)" }}>สถานะข้อมูล</span>
        <span className="mono caption" style={{ color: healthy === total ? "var(--good)" : "var(--warn)" }}>
          {healthy}/{total} HEALTHY
        </span>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        {unhealthy.slice(0, 5).map((a) => {
          const isDown = a.status === "down";
          const statusColor = isDown ? "var(--bad)" : "var(--warn)";
          const note = a.lastErrorMessage;
          const isMissingKey = note?.startsWith("Missing") ?? false;
          return (
            <div
              key={a.name}
              className="spread"
              style={{ gap: 8, alignItems: "flex-start", borderLeft: `2px solid ${statusColor}`, paddingLeft: 6 }}
              title={note ?? `${a.name}: ${a.status}`}
            >
              <span className="mono" style={{ fontSize: "var(--size-eyebrow)", color: statusColor, fontWeight: 700, letterSpacing: "0.07em", whiteSpace: "nowrap" }}>
                {isDown ? "▲" : "◆"} {a.name}
              </span>
              <span className="mono caption" style={{ color: "var(--text-3)", flex: 1, textAlign: "right" }}>
                {isMissingKey ? "KEY MISSING" : a.status.toUpperCase()}
              </span>
            </div>
          );
        })}
        {unhealthy.length > 5 && (
          <span className="mono caption" style={{ color: "var(--text-3)", paddingLeft: 6 }}>
            +{unhealthy.length - 5} more — see SOURCES
          </span>
        )}
      </div>
    </div>
  );
}
